import { fetchQuotes } from '../../lib/api/quotes';
import { apiUrl } from '../../config/runtime';
import type { LibrarySymbolInfo, RealtimeCallback } from '../../types/datafeed-api';
import { fetchCandles } from '../../lib/api/candles';
import { applyQuoteToCandles, intervalSeconds, resolutionMap, toBar, truncateToInterval } from './history';
import { getLastBar, setLastBar } from './lastBars';

interface SubscriptionTimers {
  timeout: number | null;
  interval: number | null;
  events: EventSource | null;
}

interface CandlePriceEvent {
  symbol: string;
  interval: string;
  time: number;
  price: number;
  updated_at: number;
}

const subscriptions = new Map<string, SubscriptionTimers>();
const livePollMs = 5_000;

export function subscribeBars(symbolInfo: LibrarySymbolInfo, resolution: string, onRealtimeCallback: RealtimeCallback, subscriberUID: string): void {
  const interval = resolutionMap[resolution];
  if (!interval) return;


  const cachedBar = getLastBar(symbolInfo.ticker, interval);
  if (cachedBar) onRealtimeCallback(cachedBar);

  const poll = async () => {
    try {
      const [candles, quotes] = await Promise.all([
        fetchCandles({ symbol: symbolInfo.ticker, interval, limit: 2 }),
        fetchQuotes([symbolInfo.ticker]),
      ]);
      const quote = quotes[0];
      if (quote) applyQuoteToCandles(candles, interval, quote.price, quote.updated_at);
      const latest = candles.at(-1);
      if (!latest) return;

      const bar = toBar(latest);
      setLastBar(symbolInfo.ticker, interval, bar);
      onRealtimeCallback(bar);
    } catch (err) {
      console.error('subscribeBars:', err);
    }
  };


  if (typeof EventSource === 'undefined') {
    void poll();
    const intervalTimer = window.setInterval(() => void poll(), livePollMs);
    subscriptions.set(subscriberUID, { timeout: null, interval: intervalTimer, events: null });
    return;
  }

  const timers: SubscriptionTimers = { timeout: null, interval: null, events: null };
  const params = new URLSearchParams({ symbol: symbolInfo.ticker, interval });
  const events = new EventSource(apiUrl(`/api/candles/events?${params.toString()}` as `/api/${string}`), { withCredentials: true });
  timers.events = events;
  events.addEventListener('candle', (event) => {
    try {
      const update = JSON.parse(event.data) as CandlePriceEvent;
      const latestBar = applyPriceEventToBar(getLastBar(symbolInfo.ticker, interval), interval, update);
      if (!latestBar) return;
      if (timers.interval !== null) {
        window.clearInterval(timers.interval);
        timers.interval = null;
      }
      setLastBar(symbolInfo.ticker, interval, latestBar);
      onRealtimeCallback(latestBar);
    } catch (err) {
      console.error('subscribeBars candle event:', err);
    }
  });
  events.addEventListener('error', () => {
    if (timers.interval !== null) return;
    void poll();
    timers.interval = window.setInterval(() => void poll(), livePollMs);
  });
  subscriptions.set(subscriberUID, timers);
}

export function unsubscribeBars(subscriberUID: string): void {
	const timers = subscriptions.get(subscriberUID);
	if (timers?.timeout) window.clearTimeout(timers.timeout);
	if (timers?.interval) window.clearInterval(timers.interval);
	timers?.events?.close();
	subscriptions.delete(subscriberUID);
}

function applyPriceEventToBar(bar: ReturnType<typeof getLastBar>, interval: string, update: CandlePriceEvent): ReturnType<typeof getLastBar> {
  if (!Number.isFinite(update.price) || update.price <= 0 || !Number.isFinite(update.updated_at) || update.updated_at <= 0) return bar;
  const updatedAtBucket = truncateToInterval(update.updated_at, interval) * 1000;
  const eventBucket = Number.isFinite(update.time) && update.time > 0 ? update.time * 1000 : updatedAtBucket;
  return applyPriceToBar(bar, interval, update.price, eventBucket);
}

function applyPriceToBar(bar: ReturnType<typeof getLastBar>, interval: string, price: number, bucket: number): ReturnType<typeof getLastBar> {
  if (!bar || !Number.isFinite(price) || price <= 0 || !Number.isFinite(bucket) || bucket <= 0) return bar;
  if (bucket < bar.time) return bar;
  const seconds = intervalSeconds(interval);
  if (seconds > 0 && bucket - bar.time > seconds * 1000) return bar;
  if (bucket > bar.time) {
    return {
      time: bucket,
      open: bar.close,
      high: Math.max(bar.close, price),
      low: Math.min(bar.close, price),
      close: price,
      volume: 0,
    };
  }
  return {
    ...bar,
    high: Math.max(bar.high, price),
    low: bar.low === 0 ? price : Math.min(bar.low, price),
    close: price,
  };
}
